/* eslint-disable react/style-prop-object */
import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Badge, Dropdown, Button, PaginationControls, Table, DialogNew } from "@optimizely/axiom";

import { useUserGroups } from "../../../hooks/useUserGroups/useUserGroups";
import { UserGroup } from "../../../domain/UserGroup";
import "./UserGroups.css";
import { UserGroupForm } from "../../components/UserGroupForm/UserGroupForm/UserGroupForm";
import { Sidebar } from "../../components/Sidebar/Sidebar";
import { Flags } from "../../../feature-flags/flags";
import { LoadingIndicator } from "../../components/LoadingIndicator/LoadingIndicator";
import { useUserGroupContext } from "../../components/UserGroupForm/UserGroupFormContext/UserGroupContext";
import { MoreMenu } from "../../components/MoreMenu/MoreMenu";
import { UserGroupTypeBadge } from "../../components/UserGroupTypeBadge/UserGroupTypeBadge";
import {
    ADMINCENTER_GROUP_NAME,
    ATTRIBUTE_ROLES,
    DEFAULT_DEBOUNCE,
    EVERYONE_GROUP_NAME,
    GROUP_TYPES,
    REFETCH_PERMISSIONS_EVENT_NAME,
    ALL_PAGES_SIZE,
    GROUP_TYPES_FILTER_IDS,
    GROUP_TYPES_FILTER_QUERY_PARAM,
    GROUP_FILTERS_PRODUCT_QUERY_PARAM,
    GROUP_FILTERS_INSTANCE_QUERY_PARAM,
    GROUP_FILTERS_ROLE_QUERY_PARAM,
    FAILED_OPERATION_TYPES
} from "../../../constants/constants";
import { useDebounce } from "../../../hooks/useDebounce/useDebounce";
import { SearchInput } from "../../components/SearchInput/SearchInput";
import { useUserContext } from "../../../providers/UserProvider";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag/useFeatureFlag";
import { useGroupsByUser } from "../../../hooks/useGroupsByUser/useGroupsByUser";
import { ResetFilterPrompt } from "../../components/ResetFilterPrompt/ResetFilterPrompt";
import { datadogRum } from "@datadog/browser-rum";
import { listIsDirty } from "../../../lib/utils";
import LimitByRole from "../../components/LimitByRole/LimitByRole";
import { useOrgUserGroups } from "../../../hooks/useOrgUserGroups/useOrgUserGroups";
import { InstancePermission } from "../../../domain/InstancePermission";
import { useProducts } from "../../../hooks/useProducts/useProducts";
import { useRoles } from "../../../hooks/useRoles/useRoles";
import { getEntitledInstanceIds } from "../../../lib/access-helpers";
import { emitToast } from "../../../lib/toaster-utils";
import { useQuery } from "../../../hooks/useQuery/useQuery";
import { GroupFilters } from "../../components/GroupFilters/GroupFilters";
import { IGroupFilters } from "../../../types/GroupFilters";
import { IGroupFilterQueryParams } from "../../../types/IGroupFilterQueryParams";

const UserGroups = () => {
    const [showGroupForm, setShowGroupForm] = useState(false);
    const [groupSelectedForDeletion, setGroupSelectedForDeletion] = useState<UserGroup | null>(null);
    const [loading, setLoading] = useState(false);
    const [usersToUpdate, setUsersToUpdate] = useState<string[]>([]);

    const { enabled: hideProductGroups } = useFeatureFlag(Flags.HIDE_PRODUCT_GROUPS);
    const { enabled: enableMultiSelectGroupTypeFilters } = useFeatureFlag(Flags.ENABLE_MULTISELECT_GROUP_TYPE_FILTERS);
    const { enabled: enableGranularGroupFilters } = useFeatureFlag(Flags.ENABLE_GRANULAR_GROUP_FILTERS);
    const { organizationId, profile } = useUserContext();
    const { userGroupState, updateUserGroupState } = useUserGroupContext();
    const { userGroup: selectedGroup } = userGroupState;
    const reloadedParams = useRef(false);

    const ALL_GROUP_TYPES = "All";
    const defaultGroupTypes = useMemo(() => ["Internal", "Custom"], []);
    const [groupType, setGroupType] = useState(ALL_GROUP_TYPES);
    const [groupTypes, setGroupTypes] = useState<string[]>([]);
    const [filters, setFilters] = useState<IGroupFilters>({
        groupTypes: enableMultiSelectGroupTypeFilters ? defaultGroupTypes : [ALL_GROUP_TYPES]
    });

    const allGroupTypesSelected = groupType === ALL_GROUP_TYPES;

    const queryParams = useQuery();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const memoizedQueryParamFilters = useMemo(() => {
        return {
            [GROUP_TYPES_FILTER_QUERY_PARAM]: queryParams.get(GROUP_TYPES_FILTER_QUERY_PARAM),
            [GROUP_FILTERS_PRODUCT_QUERY_PARAM]: queryParams.get(GROUP_FILTERS_PRODUCT_QUERY_PARAM),
            [GROUP_FILTERS_INSTANCE_QUERY_PARAM]: queryParams.get(GROUP_FILTERS_INSTANCE_QUERY_PARAM),
            [GROUP_FILTERS_ROLE_QUERY_PARAM]: queryParams.get(GROUP_FILTERS_ROLE_QUERY_PARAM)
        } as IGroupFilterQueryParams;
    }, [queryParams]);

    const handleReplaceQueryParam = useCallback(
        ({ param, value }: { param: string; value: string | number }) => {
            const newSearchParams = queryParams;

            const currentValue = queryParams.get(param);
            if (currentValue !== value) {
                if (!value) {
                    newSearchParams.delete(param);
                } else {
                    newSearchParams.set(param, `${value}`);
                }
                navigate({
                    pathname: pathname,
                    search: newSearchParams.toString()
                });
            }
        },
        [navigate, pathname, queryParams]
    );

    useEffect(() => {
        // granular filters are handled in a separate use effect.
        if (enableGranularGroupFilters) return;
        const groupTypeFilterFromParam = queryParams.get(GROUP_TYPES_FILTER_QUERY_PARAM)?.split(",");
        const savedGroupFilters = localStorage.getItem(GROUP_TYPES_FILTER_IDS);
        if (groupTypeFilterFromParam && groupTypeFilterFromParam.length > 0) {
            // legacy support
            setGroupTypes(groupTypeFilterFromParam);
        } else if (savedGroupFilters) {
            handleReplaceQueryParam({ param: GROUP_TYPES_FILTER_QUERY_PARAM, value: savedGroupFilters });
            const savedGroupTypesFilter = savedGroupFilters.split(",");
            setGroupTypes(savedGroupTypesFilter);
        } else {
            setGroupTypes(defaultGroupTypes);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryParams, enableGranularGroupFilters]);

    // Sync groupTypes to filters.groupTypes when granular filters are disabled
    useEffect(() => {
        if (!enableGranularGroupFilters && enableMultiSelectGroupTypeFilters && groupTypes.length > 0) {
            setFilters((prev) => ({
                ...prev,
                groupTypes: groupTypes
            }));
        }
    }, [groupTypes, enableGranularGroupFilters, enableMultiSelectGroupTypeFilters]);

    const {
        debouncedValue: debouncedSearchQuery,
        value: searchQuery,
        setValue: setSearchQuery
    } = useDebounce<string>("", DEFAULT_DEBOUNCE);
    // Transform granular filters to useUserGroups format
    const transformedFilters = useMemo(() => {
        if (!enableGranularGroupFilters) {
            return {
                groupType:
                    enableMultiSelectGroupTypeFilters || hideProductGroups
                        ? undefined
                        : !allGroupTypesSelected
                        ? groupType
                        : "",
                groupTypes: enableMultiSelectGroupTypeFilters
                    ? groupTypes
                    : !hideProductGroups
                    ? []
                    : !allGroupTypesSelected
                    ? [groupType]
                    : defaultGroupTypes
            };
        }

        // When granular filters are enabled, transform the filters object
        return {
            instanceIds: filters.instance ? [filters.instance.id] : [],
            groupType: undefined, // Don't use legacy groupType
            groupTypes: filters.groupTypes || defaultGroupTypes,
            productIds: filters.product ? [filters.product.id] : [],
            roleIds: filters.role ? [filters.role?.id] : []
            // Add other filter transformations as needed
        };
    }, [
        enableGranularGroupFilters,
        filters,
        groupTypes,
        groupType,
        allGroupTypesSelected,
        enableMultiSelectGroupTypeFilters,
        hideProductGroups,
        defaultGroupTypes
    ]);

    const {
        userGroups,
        isLoading,
        isSearching,
        // TODO: fix error handling for SWR methods other than GET; currently
        // only the initial GET of a user group is going to populate this `error` object
        error,
        currentPage,
        setCurrentPage,
        addUserGroup,
        deleteUserGroup,
        updateUserGroup,
        pageSize,
        totalCount
    } = useUserGroups({
        organizationId,
        query: debouncedSearchQuery.length > 2 ? debouncedSearchQuery : "",
        ...transformedFilters
    });

    const { enabled: filterGroupsPerEntitlements } = useFeatureFlag(Flags.FILTER_GROUPS_AND_ROLES_PER_ENTITLEMENTS);

    const { roles, isLoading: rolesLoading } = useRoles({
        organizationId: organizationId,
        size: ALL_PAGES_SIZE
    });
    const { products = [], instances = [], getUserGroupInstances } = useProducts({ organizationId });

    const entitledInstanceIds = useMemo(() => getEntitledInstanceIds({ instances }), [instances]);

    let filteredUserGroups = userGroups;
    if (filterGroupsPerEntitlements) {
        // Entitlement is decided by the instance each permission points at, not by its role ids. The
        // role list is fetched with a page cap (ALL_PAGES_SIZE) that large orgs exceed, so roles past
        // the cap read as unentitled and hid groups the API had returned. A permission's instance
        // implies its product, so the instance is both complete and the more direct signal.
        const isInternal = ({ userGroup }: { userGroup: UserGroup }) => userGroup.groupType === GROUP_TYPES.INTERNAL;
        const hasNoInstancePermissions = ({ userGroup }: { userGroup: UserGroup }) =>
            userGroup.instancePermissions.length === 0;
        // A permission with no role ids is unrestricted admin access and has always passed this
        // filter regardless of entitlement. Keeping it is deliberate: this PR fixes groups being
        // wrongly hidden, so it must not hide anything the current code shows. Tightening the
        // carve-out is a separate, visibility-narrowing change that needs its own product decision
        // — see EditGroup.cy.ts and ProductPermissionRemoval.cy.ts, which both mock `Roles: []`.
        const hasEntitledInstance = ({ userGroup }: { userGroup: UserGroup }) =>
            userGroup.instancePermissions.some(
                (permission) => permission.roleIds.length === 0 || entitledInstanceIds.has(permission.instanceId)
            );

        filteredUserGroups = userGroups?.filter(
            (userGroup) =>
                isInternal({ userGroup }) ||
                hasNoInstancePermissions({ userGroup }) ||
                hasEntitledInstance({ userGroup })
        );
    }

    const { revalidate: revalidateOrgUserGroups } = useOrgUserGroups({ organizationId });

    const { revalidate } = useGroupsByUser({
        organizationId: organizationId || "",
        email: usersToUpdate[0]
    });

    const resetGranularFilters = () => {
        const resetFilters = {
            groupTypes: enableMultiSelectGroupTypeFilters ? defaultGroupTypes : [ALL_GROUP_TYPES],
            product: undefined,
            instance: undefined,
            role: undefined
        };
        setFilters(resetFilters);
        // Clear query parameters when resetting filters
        navigate({ pathname, search: "" });
    };

    useEffect(() => {
        // repopulate filters from query params if available
        if (!products?.length || rolesLoading || reloadedParams.current) return;

        const updatedFilters: IGroupFilters = {
            groupTypes: enableMultiSelectGroupTypeFilters
                ? memoizedQueryParamFilters[GROUP_TYPES_FILTER_QUERY_PARAM]?.split(",") || defaultGroupTypes
                : [ALL_GROUP_TYPES]
        };

        const groupTypeFilters = memoizedQueryParamFilters[GROUP_TYPES_FILTER_QUERY_PARAM];
        const productId = memoizedQueryParamFilters[GROUP_FILTERS_PRODUCT_QUERY_PARAM];
        const instanceId = memoizedQueryParamFilters[GROUP_FILTERS_INSTANCE_QUERY_PARAM];
        const roleId = memoizedQueryParamFilters[GROUP_FILTERS_ROLE_QUERY_PARAM];

        if (groupTypeFilters) {
            updatedFilters.groupTypes = groupTypeFilters?.split(",") || defaultGroupTypes;
        } else if (enableGranularGroupFilters) {
            // If no query params, check localStorage for saved filters
            const savedGroupFilters = localStorage.getItem(GROUP_TYPES_FILTER_IDS);
            if (savedGroupFilters) {
                updatedFilters.groupTypes = savedGroupFilters.split(",");
            }
        }

        if (productId) {
            const product = products?.find((p) => p.id === productId);
            if (product) {
                updatedFilters.product = product;

                if (instanceId) {
                    const instance = product.instances.find((i) => i.id === instanceId);
                    if (instance) {
                        updatedFilters.instance = instance;
                    }
                }
            }
        }

        if (roleId) {
            const role = roles?.find((r) => r.id === roleId);
            if (role) {
                updatedFilters.role = role;
            }
        }

        reloadedParams.current = true;

        // Only set group types if we have actual granular filter query parameters
        if (groupTypeFilters && enableGranularGroupFilters) {
            setGroupTypes(updatedFilters.groupTypes || defaultGroupTypes);
        }

        setFilters((prev) => ({ ...prev, ...updatedFilters }));
    }, [
        defaultGroupTypes,
        enableGranularGroupFilters,
        enableMultiSelectGroupTypeFilters,
        memoizedQueryParamFilters,
        products,
        roles,
        rolesLoading
    ]);

    const totalPages = Math.ceil(totalCount / pageSize);
    const showPagination = totalPages >= 2;

    const { enabled: showUserGroupOwnerColumn } = useFeatureFlag(Flags.SHOW_USER_GROUP_OWNER_COLUMN_FLAG);
    const { enabled: showUserGroupUserCountColumn } = useFeatureFlag(Flags.SHOW_USER_GROUP_USER_COUNT_COLUMN_FLAG);

    const memoizedRevalidate = useCallback(revalidate, [revalidate]);

    useEffect(() => {
        if (selectedGroup) {
            setShowGroupForm(true);
        }
    }, [selectedGroup]);

    useEffect(() => {
        return () =>
            updateUserGroupState({
                userGroup: null,
                productInstances: [],
                users: [],
                description: ""
            });
    }, [updateUserGroupState]);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchQuery, setCurrentPage]);

    useEffect(() => {
        const [firstUserToUpdate, ...remainingUsersToUpdate] = usersToUpdate;

        if (!firstUserToUpdate) return;

        memoizedRevalidate();
        setUsersToUpdate(remainingUsersToUpdate);
    }, [usersToUpdate, memoizedRevalidate]);

    const showForm = () => {
        setShowGroupForm(true);
    };

    const openViewUserGroup = ({
        userGroup: userGroupToView,
        editMode = false
    }: {
        editMode?: boolean;
        userGroup: UserGroup;
    }) => {
        updateUserGroupState({
            userGroup: userGroupToView,
            originalUserGroup: userGroupToView,
            editing: editMode,
            description: userGroupToView.description,
            initialUsers: []
        });
    };

    const handleFormCancel = () => {
        // reset form
        updateUserGroupState({
            userGroup: null,
            description: "",
            productInstances: [],
            users: [],
            initialUsers: []
        });
        setShowGroupForm(false);
    };

    const goToPage = (page: number) => {
        handleFormCancel();
        setCurrentPage(page);
    };

    const updateStaleUsersData = ({
        previousUsers,
        updatedUsers
    }: {
        previousUsers: string[];
        updatedUsers: string[];
    }) => {
        const usersToUpdate = Array.from(new Set([...previousUsers, ...updatedUsers]));
        setUsersToUpdate(usersToUpdate);
    };

    const updateAppropriateUsers = ({
        previousGroupName,
        previousGroupPermissions,
        previousUsers,
        updatedGroupName,
        updatedGroupPermissions,
        updatedUsers
    }: {
        previousGroupName: string;
        previousGroupPermissions: InstancePermission[];
        previousUsers: string[];
        updatedGroupName: string;
        updatedGroupPermissions: InstancePermission[];
        updatedUsers: string[];
    }) => {
        if (previousGroupName !== updatedGroupName) return updateStaleUsersData({ previousUsers, updatedUsers });

        const groupPermissionsHadChanged =
            new Set(previousGroupPermissions).size !== new Set(updatedGroupPermissions).size;
        if (groupPermissionsHadChanged) return updateStaleUsersData({ previousUsers, updatedUsers });

        const usersToUpdate = updatedUsers
            .filter((user) => !previousUsers.includes(user))
            .concat(previousUsers.filter((user) => !updatedUsers.includes(user)));

        setUsersToUpdate(usersToUpdate);
    };

    const handleGranularGroupFilterQueryParams = (updatedFilters: IGroupFilters) => {
        const newParams = new URLSearchParams();
        if (
            updatedFilters.groupTypes &&
            updatedFilters.groupTypes.length > 0 &&
            !updatedFilters.groupTypes.includes(ALL_GROUP_TYPES)
        ) {
            newParams.set(GROUP_TYPES_FILTER_QUERY_PARAM, updatedFilters.groupTypes.join(","));
        }

        if (updatedFilters.product?.id) {
            newParams.set(GROUP_FILTERS_PRODUCT_QUERY_PARAM, updatedFilters.product.id);
        }

        if (updatedFilters.instance?.id) {
            newParams.set(GROUP_FILTERS_INSTANCE_QUERY_PARAM, updatedFilters.instance.id);
        }

        if (updatedFilters.role?.id) {
            newParams.set(GROUP_FILTERS_ROLE_QUERY_PARAM, updatedFilters.role.id);
        }

        navigate({ pathname, search: newParams.toString() });
    };

    // TODO: This is seperated from cancel in case we need to do additional distinctions like show a toast / success
    // message. If not needed should be combined with `handleFormCancel` and renamed.
    const handleFormCompletion = ({
        previousUserGroup,
        updatedUserGroup
    }: {
        previousUserGroup: UserGroup | null;
        updatedUserGroup: UserGroup | null;
    }): Promise<any> => {
        if (!updatedUserGroup) {
            return updateUserGroupState({
                userGroup: null,
                productInstances: [],
                users: [],
                description: ""
            });
        }

        if (previousUserGroup) {
            const sendEvent =
                profile?.email &&
                (previousUserGroup.users.includes(profile?.email) !== updatedUserGroup.users.includes(profile?.email) ||
                    listIsDirty(
                        previousUserGroup.instancePermissions.map((i) => i.instanceId),
                        updatedUserGroup.instancePermissions.map((i) => i.instanceId)
                    ));

            const staleDataToUpdate = {
                previousGroupName: previousUserGroup.name,
                previousGroupPermissions: previousUserGroup.instancePermissions,
                previousUsers: previousUserGroup.users,
                updatedGroupName: updatedUserGroup.name,
                updatedGroupPermissions: updatedUserGroup.instancePermissions,
                updatedUsers: updatedUserGroup.users
            };
            return updateUserGroup({ previousUserGroup, updatedUserGroup })
                .then(async ({ userGroup: updatedGroup, failures }) => {
                    const permissionDeleteFailures =
                        failures[FAILED_OPERATION_TYPES.USER_GROUP_DELETE_INSTANCE_PERMISSION] || [];
                    const permissionAddFailures =
                        failures[FAILED_OPERATION_TYPES.USER_GROUP_ADD_INSTANCE_PERMISSION] || [];
                    const permissionUpdateFailures =
                        failures[FAILED_OPERATION_TYPES.USER_GROUP_UPDATE_INSTANCE_PERMISSION] || [];

                    const hasInstancePermissionFailures =
                        permissionDeleteFailures.length > 0 ||
                        permissionAddFailures.length > 0 ||
                        permissionUpdateFailures.length > 0;

                    // Check if ALL failures are eventual consistency warnings (not real errors)
                    const isEventualConsistencyError = (failure: any) => {
                        const message = failure?.error?.message || "";
                        return message.includes("eventual consistency");
                    };

                    const allFailuresAreEventualConsistency =
                        hasInstancePermissionFailures &&
                        [...permissionDeleteFailures, ...permissionAddFailures, ...permissionUpdateFailures].every(
                            isEventualConsistencyError
                        );

                    if (hasInstancePermissionFailures && !allFailuresAreEventualConsistency) {
                        // Re-fetch product instances to get the complete data for instances that had failures
                        const instances = await getUserGroupInstances({ userGroupId: updatedGroup.id });

                        // Use the updatedGroup returned from the service layer - it already has
                        // the correct state reflecting what succeeded/failed
                        updateUserGroupState({
                            userGroup: updatedGroup,
                            productInstances: instances?.items || [],
                            failures,
                            loading: false
                        });

                        return { userGroup: updatedGroup, failures, productInstances: instances?.items || [] };
                    }

                    // Full success path (including eventual consistency warnings)
                    revalidateOrgUserGroups();
                    updateAppropriateUsers(staleDataToUpdate);
                    if (sendEvent) {
                        window.dispatchEvent(new CustomEvent(REFETCH_PERMISSIONS_EVENT_NAME));
                    }

                    updateUserGroupState({
                        userGroup: null,
                        productInstances: [],
                        users: [],
                        description: ""
                    });
                    setShowGroupForm(false);
                    return { userGroup: updatedGroup, failures };
                })
                .catch((e) => {
                    console.error(e);
                    datadogRum.addError(e);
                    throw e;
                });
        }

        return addUserGroup(updatedUserGroup).then((res) => {
            revalidateOrgUserGroups();
            updateStaleUsersData({ previousUsers: [], updatedUsers: updatedUserGroup.users });
            updateUserGroupState({ description: "" });
            setShowGroupForm(false);
            window.dispatchEvent(new CustomEvent(REFETCH_PERMISSIONS_EVENT_NAME));
            return res;
        });
    };

    const deleteGroup = () => {
        setLoading(true);
        deleteUserGroup({ userGroupId: groupSelectedForDeletion!.id })
            .then(() => {
                const removedIndex = filteredUserGroups!.findIndex(
                    (group) => group.id === groupSelectedForDeletion!.id
                );
                const previousUsers =
                    filteredUserGroups!.find((group) => group.id === groupSelectedForDeletion!.id)?.users || [];
                filteredUserGroups!.splice(removedIndex, 1);
                emitToast({ message: "Group successfully deleted." });
                setLoading(false);
                revalidateOrgUserGroups();
                window.dispatchEvent(new CustomEvent(REFETCH_PERMISSIONS_EVENT_NAME));
                updateStaleUsersData({ previousUsers, updatedUsers: [] });
                setGroupSelectedForDeletion(null);
            })
            .catch((e) => {
                console.error(e);
                datadogRum.addError(e);
                setLoading(false);
            });
    };
    const showLoading = useMemo(() => {
        // Always show loading during initial data fetching or searching
        if (isSearching || isLoading) {
            return true;
        }

        // If granular filters are enabled, wait for dependent data to load
        if (enableGranularGroupFilters) {
            // Wait for roles to load (needed for filter setup)
            if (rolesLoading) {
                return true;
            }

            // Wait for products to be available (needed for filter setup)
            if (!products?.length && organizationId) {
                return true;
            }
        }

        return false;
    }, [isSearching, isLoading, enableGranularGroupFilters, rolesLoading, products, organizationId]);

    if (isLoading && !isSearching) return <LoadingIndicator height="100%" type="spinner" />;

    if (error) {
        console.error(error);
        datadogRum.addError(error);
        return <div>Error fetching user group.</div>;
    }

    const handleFiltersChange = (updatedFilters: IGroupFilters) => {
        setFilters(updatedFilters);
        handleGranularGroupFilterQueryParams(updatedFilters);
    };

    return (
        // <LimitByRole mode="unauthorized" action={ATTRIBUTE_ROLES.GROUPS.READ}>
        <div className="usergroup-list-page list-page">
            <div className={`usergroup-list-bar ${enableGranularGroupFilters ? "usergroup-list-bar--no-bottom" : ""}`}>
                <div className="left-grouping">
                    <div className="usergroup-search">
                        <SearchInput
                            placeholder="Search groups by name..."
                            type="text"
                            value={searchQuery}
                            onChange={({ value }) => {
                                setSearchQuery(value);
                            }}
                        />
                    </div>
                    {!enableGranularGroupFilters && (
                        <div className="group-type-filter">
                            <GroupFilters
                                hideProductGroupFilter={hideProductGroups}
                                onChange={handleFiltersChange}
                                selectedFilters={filters}
                            />
                        </div>
                    )}
                </div>
                <LimitByRole action={ATTRIBUTE_ROLES.GROUPS.CREATE} mode="hide">
                    <div className="add-usergroup">
                        <Button style="highlight" onClick={showForm}>
                            Add Group...
                        </Button>
                    </div>
                </LimitByRole>
            </div>

            {enableGranularGroupFilters && (
                <GroupFilters
                    className="push-double--top push-triple--bottom"
                    hideProductGroupFilter={hideProductGroups}
                    onChange={handleFiltersChange}
                    selectedFilters={filters}
                />
            )}

            <Sidebar heading={selectedGroup ? "Group" : "Add Group"} isOpen={showGroupForm} onCancel={handleFormCancel}>
                <UserGroupForm onCancel={handleFormCancel} onSubmit={handleFormCompletion} />
            </Sidebar>

            <div className="usergroup-list-table list-table flex flex--column">
                {showLoading && <LoadingIndicator height="100%" type="spinner" overlay />}
                {!(showLoading || filteredUserGroups?.length) && (
                    <ResetFilterPrompt
                        onClick={() => {
                            setSearchQuery("");
                            setGroupType(ALL_GROUP_TYPES);
                            resetGranularFilters();
                        }}
                        prompt="No user groups match your filter."
                    />
                )}
                {!showLoading && filteredUserGroups && filteredUserGroups.length > 0 && (
                    <>
                        <div className="user-manager__table">
                            <Table density="loose" style="rule-no-bottom-border">
                                <Table.THead>
                                    <Table.TR>
                                        <Table.TH sorting={{}} textAlign="left" width="50%">
                                            Name
                                        </Table.TH>
                                        {showUserGroupOwnerColumn && (
                                            <Table.TH sorting={{}} width="20%">
                                                Owner
                                            </Table.TH>
                                        )}
                                        {showUserGroupUserCountColumn && (
                                            <Table.TH sorting={{}} width="8%">
                                                Users
                                            </Table.TH>
                                        )}
                                        <Table.TH sorting={{}} width="10%">
                                            Group Type
                                        </Table.TH>
                                        <Table.TH sorting={{}} width="18%">
                                            Created
                                        </Table.TH>
                                        <Table.TH sorting={{}} width="18%">
                                            Modified
                                        </Table.TH>
                                        <Table.TH sorting={{}} width="60px" />
                                    </Table.TR>
                                </Table.THead>
                                <Table.TBody>
                                    {filteredUserGroups?.map((userGroup) => {
                                        const hideAddUserOption = userGroup.groupType === GROUP_TYPES.INTERNAL;
                                        const hideDeleteOption =
                                            [EVERYONE_GROUP_NAME, ADMINCENTER_GROUP_NAME].includes(userGroup?.name) ||
                                            (userGroup.groupType &&
                                                [GROUP_TYPES.INTERNAL, GROUP_TYPES.PRODUCT].includes(
                                                    userGroup.groupType as GROUP_TYPES
                                                ));
                                        const hideMoreMenu = hideAddUserOption && hideDeleteOption;

                                        return (
                                            <Table.TR
                                                key={userGroup.id}
                                                isHighlighted={selectedGroup?.id === userGroup.id && showGroupForm}
                                            >
                                                <Table.TD>
                                                    <button
                                                        className="link button-as-link"
                                                        type="button"
                                                        onClick={() => openViewUserGroup({ userGroup })}
                                                    >
                                                        {userGroup.name}
                                                    </button>
                                                    {userGroup.description && (
                                                        <div className="muted micro">{userGroup.description}</div>
                                                    )}
                                                </Table.TD>
                                                {showUserGroupOwnerColumn && (
                                                    <Table.TD>{userGroup.groupOwner}</Table.TD>
                                                )}
                                                {showUserGroupUserCountColumn && (
                                                    <Table.TD>
                                                        <Badge>{userGroup.userCount}</Badge>
                                                    </Table.TD>
                                                )}
                                                <Table.TD>
                                                    <UserGroupTypeBadge groupType={userGroup.groupType} />
                                                </Table.TD>
                                                <Table.TD>
                                                    {userGroup.created.toLocaleString("en-US", {
                                                        year: "numeric",
                                                        day: "numeric",
                                                        month: "short",
                                                        hour12: true,
                                                        hour: "numeric",
                                                        minute: "numeric"
                                                    })}
                                                </Table.TD>
                                                <Table.TD>
                                                    {userGroup.modified?.toLocaleString("en-US", {
                                                        year: "numeric",
                                                        day: "numeric",
                                                        month: "short",
                                                        hour12: true,
                                                        hour: "numeric",
                                                        minute: "numeric"
                                                    })}
                                                </Table.TD>
                                                <LimitByRole action={ATTRIBUTE_ROLES.GROUPS.UPDATE} mode="hide">
                                                    <Table.TD
                                                        colSpan={1}
                                                        className="user-manager__table--more-menu"
                                                        data-cy={`${userGroup.name}-more-menu`}
                                                    >
                                                        {!hideMoreMenu && (
                                                            <MoreMenu>
                                                                {!hideAddUserOption && (
                                                                    <Dropdown.ListItem>
                                                                        <Dropdown.BlockLink
                                                                            onClick={() =>
                                                                                openViewUserGroup({
                                                                                    userGroup,
                                                                                    editMode: true
                                                                                })
                                                                            }
                                                                        >
                                                                            Add user to group...
                                                                        </Dropdown.BlockLink>
                                                                    </Dropdown.ListItem>
                                                                )}
                                                                {!hideDeleteOption && (
                                                                    <Dropdown.ListItem>
                                                                        <Dropdown.BlockLink
                                                                            onClick={() =>
                                                                                setGroupSelectedForDeletion(userGroup)
                                                                            }
                                                                        >
                                                                            <span className="danger">
                                                                                Delete group...
                                                                            </span>
                                                                        </Dropdown.BlockLink>
                                                                    </Dropdown.ListItem>
                                                                )}
                                                            </MoreMenu>
                                                        )}
                                                    </Table.TD>
                                                </LimitByRole>
                                            </Table.TR>
                                        );
                                    })}
                                </Table.TBody>
                            </Table>
                        </div>
                        {showPagination && (
                            <PaginationControls
                                className="anchor--bottom"
                                currentPage={currentPage}
                                goToPage={(page: number) => goToPage(page)}
                                totalPages={totalPages}
                            />
                        )}
                    </>
                )}
            </div>

            {!!groupSelectedForDeletion && (
                <DialogNew
                    children=""
                    footerButtonList={[
                        <Button
                            key={0}
                            isDisabled={loading}
                            style="plain"
                            onClick={() => setGroupSelectedForDeletion(null)}
                        >
                            Cancel
                        </Button>,
                        <Button key={1} isDisabled={loading} style="danger" onClick={deleteGroup}>
                            Delete Group
                        </Button>
                    ]}
                    subtitle="Deleting this group will cause any users in it to lose access to assigned products."
                    title="Delete Group"
                    hasCloseButton
                    hasOverlay
                    onClose={() => setGroupSelectedForDeletion(null)}
                />
            )}
        </div>
    );
};

export default UserGroups;
